import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import { Loading } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { StatCard } from "../../components/StatCard";
import { StatusPill } from "../../components/StageBar";
import { DollarSign, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export default function Money() {
  const invoices = useFetch("/invoices");
  const payments = useFetch("/payments");
  if (invoices.loading || payments.loading) return <Loading />;

  const invs = invoices.data || [];
  const overdue = invs.filter((i) => i.status === "overdue");
  const outstanding = invs.filter((i) => ["sent", "overdue"].includes(i.status));
  const paid = invs.filter((i) => i.status === "paid");

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Financials" title="Money" subtitle={`${money(overdue.reduce((a, i) => a + i.amount, 0))} needs collection across ${overdue.length} overdue invoices.`} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testid="money-outstanding" label="Outstanding" value={money(outstanding.reduce((a, i) => a + i.amount, 0))} hint={`${outstanding.length} open invoices`} icon={Clock} />
        <StatCard testid="money-overdue" label="Overdue 30+" value={money(overdue.reduce((a, i) => a + i.amount, 0))} hint={`${overdue.length} invoices`} icon={AlertTriangle} tone="danger" />
        <StatCard testid="money-collected" label="Collected" value={money(paid.reduce((a, i) => a + i.amount, 0))} hint={`${paid.length} paid`} icon={CheckCircle2} tone="success" />
      </div>

      <div className="hw-card overflow-hidden">
        <div className="border-b border-slate-800 px-6 py-4">
          <h3 className="font-head text-xl font-bold text-white">Invoices</h3>
        </div>
        <div className="divide-y divide-slate-800">
          {invs.sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0)).map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-4 px-6 py-4 transition hover:bg-slate-800/30" data-testid={`invoice-${i.id}`}>
              <div className="flex items-center gap-4">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${i.status === "overdue" ? "bg-rose-500/15 text-rose-300" : i.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`}>
                  <DollarSign size={18} />
                </div>
                <div>
                  <div className="font-semibold text-white">{i.number} · {i.customer_name}</div>
                  <div className="text-sm text-slate-400">{i.description}{i.days_overdue > 0 ? ` · ${i.days_overdue} days overdue` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-head text-xl font-bold text-white">{money(i.amount)}</span>
                <StatusPill status={i.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
